/**
 * Open a hidden iframe, write HTML into it, and trigger the browser's print dialog.
 * Shared by single-label dialog and bulk print flows.
 */
export const printViaIframe = (html: string): void => {
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = 'none';
    document.body.appendChild(iframe);

    const iframeDoc = iframe.contentDocument ?? iframe.contentWindow?.document;
    if (!iframeDoc) {
        document.body.removeChild(iframe);
        return;
    }

    iframeDoc.open();
    iframeDoc.write(html);
    iframeDoc.close();

    let printed = false;
    const iframeWindow = iframe.contentWindow;

    const triggerPrint = () => {
        if (printed) return;
        printed = true;
        iframe.contentWindow?.focus();
        // Reset zoom and ensure zero margins for precise label alignment
        if (iframeDoc?.body) {
            iframeDoc.body.style.zoom = '1';
            iframeDoc.body.style.margin = '0';
            iframeDoc.body.style.padding = '0';
        }
        // Force all margins to 0 in the document
        if (iframeDoc?.documentElement) {
            iframeDoc.documentElement.style.margin = '0';
            iframeDoc.documentElement.style.padding = '0';
        }
        iframe.contentWindow?.print();
        setTimeout(() => {
            if (document.body.contains(iframe)) document.body.removeChild(iframe);
        }, 1000);
    };

    const handleAfterPrint = () => {
        window.parent.postMessage(
            { type: "PRINT_DONE", payload: {} },
            "*"
        );
        if (document.body.contains(iframe)) {
            document.body.removeChild(iframe);
        }

    };
    iframeWindow?.addEventListener("afterprint", handleAfterPrint);
    iframe.onload = triggerPrint;
    setTimeout(() => {
        if (document.body.contains(iframe)) triggerPrint();
    }, 500);
};

/**
 * Extract QR SVGs from a label DOM element's innerHTML, ensuring each
 * has the `xmlns` attribute required for standalone SVG rendering.
 */
export const extractQrSvgHtml = (labelElement: HTMLElement): string =>
    (labelElement.innerHTML.match(/<svg[\s\S]*?<\/svg>/gi) ?? [])
        .map((svg) => (svg.includes('xmlns=') ? svg : svg.replace('<svg', '<svg xmlns="http://www.w3.org/2000/svg"')))
        .join('');

/**
 * Listen for the PRINT_DONE message from the print iframe.
 * Calls `onDone` once — either when `afterprint` fires or after a 10 s fallback.
 */
export const onPrintComplete = (onDone: () => void): void => {
    let handled = false;
    const handler = (event: MessageEvent) => {
        if (event.data?.type === 'PRINT_DONE' && !handled) {
            handled = true;
            window.removeEventListener('message', handler);
            onDone();
        }
    };
    window.addEventListener('message', handler);
    setTimeout(() => {
        if (!handled) {
            handled = true;
            window.removeEventListener('message', handler);
            onDone();
        }
    }, 10000);
};
